// src/features/feeds/hooks/useFeedGridEngine.ts

import fuzzysort from 'fuzzysort';
import { useCallback, useMemo, useRef } from 'react';
import { MediaStateType } from '@lib2/model/media/MediaTypes';
import { FeedUtils } from '../FeedUtils';
import type { SearchMode } from '@/lib/types/Media';
import type { SortDirection } from '@/lib/types/common';
import type { FeedData, FeedGridRow, FeedSortKey } from '@/lib/types/Feed';

// --- Types ---

interface SortState {
  key: FeedSortKey;
  direction: SortDirection;
}

interface SearchState {
  mode: SearchMode;
  text: string;
}

export interface UseFeedGridEngineArgs {
  customerId: number | null;
  feeds: FeedData[];
  sort: SortState;
  search: SearchState;
}

export interface UseFeedGridEngineResult {
  count: number;
  orderedIds: number[];
  getIdByIndex: (index: number) => number | undefined;
  getRowByIndex: (index: number) => FeedGridRow | null;
  resultCount: number;
}

// --- Dataset record for indexing ---

interface DatasetRecord {
  id: number;
  source: FeedData;
  title: string;
  state: string;
  created: number;
  updated: number;
  lowerTitle: string;
  displayId: string;
}

interface FuzzyPreparedRecord {
  id: number;
  preparedTitle: ReturnType<typeof fuzzysort.prepare>;
}

// --- Sort comparators ---

function compareTitleAsc(a: DatasetRecord, b: DatasetRecord): number {
  return a.lowerTitle.localeCompare(b.lowerTitle) || a.id - b.id;
}

function compareStatusAsc(a: DatasetRecord, b: DatasetRecord): number {
  // Active sorts before Inactive (a < i alphabetically)
  if (a.state !== b.state) return a.state.localeCompare(b.state);
  return a.lowerTitle.localeCompare(b.lowerTitle) || a.id - b.id;
}

function compareCreatedAsc(a: DatasetRecord, b: DatasetRecord): number {
  return (a.created - b.created) || a.id - b.id;
}

function compareUpdatedAsc(a: DatasetRecord, b: DatasetRecord): number {
  return (a.updated - b.updated) || a.id - b.id;
}

const SORT_COMPARATORS: Record<FeedSortKey, (a: DatasetRecord, b: DatasetRecord) => number> = {
  title: compareTitleAsc,
  status: compareStatusAsc,
  created: compareCreatedAsc,
  updated: compareUpdatedAsc,
};

// --- Search ---

function filterExact(records: DatasetRecord[], query: string): Set<number> {
  const lower = query.toLowerCase();
  const result = new Set<number>();
  for (const record of records) {
    if (record.lowerTitle.includes(lower) || record.displayId.includes(lower)) {
      result.add(record.id);
    }
  }
  return result;
}

function filterFuzzy(
  records: DatasetRecord[],
  preparedRecords: FuzzyPreparedRecord[],
  query: string,
): Set<number> {
  if (!query.trim()) return new Set(records.map((r) => r.id));

  const results = fuzzysort.go(query, preparedRecords, {
    key: 'preparedTitle',
    threshold: 0.3,
    limit: records.length,
  });

  return new Set(results.map((r) => r.obj.id));
}

// --- Engine hook ---

export function useFeedGridEngine({
  customerId,
  feeds,
  sort,
  search,
}: UseFeedGridEngineArgs): UseFeedGridEngineResult {
  // Build dataset records (memoized on feeds identity)
  const { records, fuzzyPrepared } = useMemo(() => {
    const recs: DatasetRecord[] = [];
    const prepared: FuzzyPreparedRecord[] = [];

    for (const feed of feeds) {
      const displayId = FeedUtils.getDisplayId(feed.feedId);
      const rec: DatasetRecord = {
        id: feed.feedId,
        source: feed,
        title: feed.title,
        state: String(feed.mediaStateType),
        created: feed.created,
        updated: feed.updated,
        lowerTitle: feed.title.toLowerCase(),
        displayId,
      };
      recs.push(rec);
      prepared.push({
        id: feed.feedId,
        preparedTitle: fuzzysort.prepare(feed.title),
      });
    }

    return { records: recs, fuzzyPrepared: prepared };
  }, [feeds]);

  // Apply search filter
  const filteredIds = useMemo(() => {
    const query = search.text.trim();
    if (!query) return null; // null = no filter, use all

    if (search.mode === 'exact') {
      return filterExact(records, query);
    }
    return filterFuzzy(records, fuzzyPrepared, query);
  }, [records, fuzzyPrepared, search.text, search.mode]);

  // Filter + sort
  const orderedIds = useMemo(() => {
    // Get filtered records
    const filtered = filteredIds
      ? records.filter((r) => filteredIds.has(r.id))
      : records;

    // Sort
    const comparator = SORT_COMPARATORS[sort.key];
    const sorted = [...filtered].sort(comparator);

    // Apply direction
    const ids = sorted.map((r) => r.id);
    if (sort.direction === 'desc') {
      ids.reverse();
    }

    return ids;
  }, [records, filteredIds, sort.key, sort.direction]);

  // Row model cache (keyed by feedId for O(1) lookup)
  const rowCacheRef = useRef(new Map<number, { source: FeedData; row: FeedGridRow }>());

  // Build lookup map for source data
  const feedById = useMemo(() => {
    const map = new Map<number, FeedData>();
    for (const feed of feeds) {
      map.set(feed.feedId, feed);
    }
    return map;
  }, [feeds]);

  const getIdByIndex = useCallback(
    (index: number) => orderedIds[index],
    [orderedIds],
  );

  const getRowByIndex = useCallback(
    (index: number): FeedGridRow | null => {
      const feedId = orderedIds[index];
      if (feedId == null) return null;

      const feed = feedById.get(feedId);
      if (!feed) return null;

      // Check cache
      const cached = rowCacheRef.current.get(feedId);
      if (cached && cached.source === feed) {
        return cached.row;
      }

      // Build row model
      const row = FeedUtils.buildRow(feed);
      rowCacheRef.current.set(feedId, { source: feed, row });

      // Evict old entries if cache grows too large
      if (rowCacheRef.current.size > 3000) {
        const activeIds = new Set(orderedIds);
        for (const key of rowCacheRef.current.keys()) {
          if (!activeIds.has(key)) {
            rowCacheRef.current.delete(key);
          }
        }
      }

      return row;
    },
    [orderedIds, feedById],
  );

  return {
    count: orderedIds.length,
    orderedIds,
    getIdByIndex,
    getRowByIndex,
    resultCount: orderedIds.length,
  };
}
